import { Image } from "@heroui/react";
import { APP_CLINIC, DOCUMENT_PRIVACY_POLICY } from "core/consts";
import { ENV_DEMO, getStaticAsset } from "core/model/config";
import { Banner, Button, ConnectedPasswordInput, Link } from "ds/ui";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoWithNamePath } from "ds_legacy/components/RecareLogo";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useTranslations } from "translations";
import { loginPage, LoginPageVariants } from "../LoginPageV2/classes";

export type PasswordResetFromEmailPageProps = LoginPageVariants & {
  goToProductionUrl: string;
  isLoading: boolean;
  onSubmit: () => void;
};

export function PasswordResetFromEmailPage({
  app = APP_CLINIC,
  env,
  goToProductionUrl,
  isLoading,
  onSubmit,
}: PasswordResetFromEmailPageProps) {
  const isDemo = env === ENV_DEMO;
  const classes = loginPage({ app, env });
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();

  return (
    <div className={classes.page()}>
      <div className={classes.container()}>
        <div className={classes.content()}>
          <div className={classes.header()}>
            <Image
              alt="Recare logo"
              src={getStaticAsset(getRecareLogoWithNamePath("master"))}
              width={160}
            />
          </div>
          <div className={classes.form()}>
            <div className={classes.formContent()}>
              <h1 className={classes.title()}>
                {isDemo && (
                  <span className="font-normal">
                    {translations.login.loginPage.titleDemo} |{" "}
                  </span>
                )}
                {translations.login.setNewPasswordTitle}
              </h1>
              <p className="text-sm" id="description">
                {translations.login.setNewPasswordInstructions}
              </p>
              {isDemo && (
                <Banner
                  color="warning"
                  message={
                    <p>
                      {translations.login.loginPage.environmentInfo}{" "}
                      <Link
                        aria-label={externalLinkLabel(
                          translations.login.loginPage.productionLink,
                          "website",
                        )}
                        color="primary-dark"
                        href={goToProductionUrl}
                        id="go-to-prod"
                        size="sm"
                      >
                        {translations.login.loginPage.productionLink}
                      </Link>
                      .
                    </p>
                  }
                  size="sm"
                />
              )}
            </div>
            <div className={classes.formContent()}>
              <ConnectedPasswordInput
                aria-describedby="description"
                elementName="new_password"
                label={translations.login.enterNewPasswordTitle}
                placeholder={translations.login.enterNewPasswordPlaceholder}
                required
                withIndicator
              />
              <ConnectedPasswordInput
                elementName="confirm_new_password"
                label={translations.login.confirmYourPasswordTitle}
                placeholder={translations.login.confirmNewPasswordPlaceholder}
                required
              />
            </div>
            <div className="flex justify-between">
              <Button
                color="primary"
                fullWidth
                id="set-new-password"
                isLoading={isLoading}
                size="lg"
                type="submit"
                variant="solid"
                onPress={onSubmit}
              >
                {translations.login.buttonSetNewPassword}
              </Button>
            </div>
            <Banner
              color="warning"
              message={translations.login.setNewPasswordEncryptionInformation}
              size="sm"
            />
          </div>
        </div>
        <div className={classes.footer()}>
          <hr className={classes.footerDivider()} />
          <p>
            {translations.login.loginPage.privacyPolicyInfo}{" "}
            <Link
              aria-label={externalLinkLabel(
                translations.login.privacyPolicy,
                "pdf",
              )}
              color="primary-dark"
              href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
              id="privacy-policy"
              isExternal
              size="sm"
            >
              {translations.login.privacyPolicy}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
